'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { createClient } from '../../lib/supabase/client';

const SUPER_ADMIN_EMAIL = 'superadmin@sellnex.net';
const TENANT_ADMIN_ROLES = ['business_admin', 'tenant_admin', 'super_admin'];

type AccessState = 'pending' | 'allowed' | 'denied';

export default function AdminLayout({ children }: { children: React.ReactNode }) {
  const { user, loading, roleLoading, isAdmin, isSuperAdmin } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const [accessState, setAccessState] = useState<AccessState>('pending');
  const checkedRef = useRef(false);

  // /admin/super-login is fully public — skip all role checks
  const isSuperLoginPage = pathname === '/admin/super-login' || pathname.startsWith('/admin/super-login/');
  const isBypassEmail = user?.email === SUPER_ADMIN_EMAIL;
  const authReady = !loading && !roleLoading;

  useEffect(() => {
    // Reset check when user changes
    checkedRef.current = false;
    setAccessState('pending');
  }, [user?.id]);

  useEffect(() => {
    if (isSuperLoginPage) {
      setAccessState('allowed');
      return;
    }

    // Wait for auth to finish loading
    if (!authReady) return;

    // Prevent duplicate checks
    if (checkedRef.current) return;
    checkedRef.current = true;

    // Not logged in → deny
    if (!user) {
      setAccessState('denied');
      return;
    }

    // Email bypass or already an admin via user_profiles
    if (isBypassEmail || isAdmin || isSuperAdmin) {
      setAccessState('allowed');
      return;
    }

    // Check user_tenant_memberships for tenant admin roles
    const checkTenantMembership = async () => {
      try {
        const supabase = createClient();
        const { data: memberships } = await supabase
          .from('user_tenant_memberships')
          .select('role, is_active')
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (memberships && memberships.length > 0) {
          const hasTenantAdmin = memberships.some((m) =>
            TENANT_ADMIN_ROLES.includes(m.role?.toLowerCase?.() ?? '')
          );
          if (hasTenantAdmin) {
            setAccessState('allowed');
            return;
          }
        }

        setAccessState('denied');
      } catch {
        // On error, deny access rather than loop
        setAccessState('denied');
      }
    };

    checkTenantMembership();
  }, [authReady, user, isBypassEmail, isAdmin, isSuperAdmin, isSuperLoginPage]);

  // Redirect denied users — only after access state is resolved
  useEffect(() => {
    if (accessState === 'denied') {
      router.replace('/sign-up-login-screen');
    }
  }, [accessState, router]);

  // Always render super-login page without any auth gate
  if (isSuperLoginPage) return <>{children}</>;

  // Show spinner while auth or membership check is in progress
  if (!authReady || accessState === 'pending') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (accessState !== 'allowed') return null;

  return <>{children}</>;
}
